import { addDays, format, startOfMonth } from "date-fns";
import { Sprout } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useMemo, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { PersonalEvent, PersonalTodo } from "../types";
import { getMonthGridRange } from "./calendarRange";
import { DayTimeline } from "./DayTimeline";
import { EventDialog } from "./EventDialog";
import { LifeWheelCard } from "./LifeWheelCard";
import { MonthCalendar } from "./MonthCalendar";
import { NotificationPermissionButton } from "./NotificationPermissionButton";
import { TodoDialog } from "./TodoDialog";
import { TodoList } from "./TodoList";
import { toDateOnly } from "./todoSchedule";
import { useDateLocale } from "./useDateLocale";

const MAX_ITEMS = 1000;

type EventDialogState =
  | { open: false }
  | { open: true; event?: PersonalEvent; start?: Date };

type TodoDialogState = { open: false } | { open: true; todo?: PersonalTodo };

const usePersonalData = (month: Date) => {
  const { identity } = useGetIdentity();
  const { dateFnsLocale } = useDateLocale();
  const enabled = identity?.id != null;
  const { start, end } = getMonthGridRange(
    month,
    dateFnsLocale.options?.weekStartsOn ?? 0,
  );

  const { data: events = [] } = useGetList<PersonalEvent>(
    "personal_events",
    {
      pagination: { page: 1, perPage: MAX_ITEMS },
      sort: { field: "start_at", order: "ASC" },
      filter: {
        sales_id: identity?.id,
        "end_at@gt": start.toISOString(),
        "start_at@lt": addDays(end, 1).toISOString(),
      },
    },
    { enabled },
  );

  const { data: todos = [] } = useGetList<PersonalTodo>(
    "personal_todos",
    {
      pagination: { page: 1, perPage: MAX_ITEMS },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: identity?.id, "done_at@is": null },
    },
    { enabled },
  );

  return {
    events,
    // A just-completed to-do can linger in the cache until the list refetches
    pendingTodos: todos.filter((todo) => !todo.done_at),
  };
};

export const PersonalPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { dateFnsLocale } = useDateLocale();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [eventDialog, setEventDialog] = useState<EventDialogState>({
    open: false,
  });
  const [todoDialog, setTodoDialog] = useState<TodoDialogState>({
    open: false,
  });
  const { events, pendingTodos } = usePersonalData(month);

  const markedDays = useMemo(
    () =>
      new Set([
        ...events.map((event) => toDateOnly(new Date(event.start_at))),
        ...pendingTodos.map((todo) => todo.due_date),
      ]),
    [events, pendingTodos],
  );

  const calendar = (
    <MonthCalendar
      month={month}
      selectedDay={selectedDay}
      markedDays={markedDays}
      onMonthChange={setMonth}
      onSelectDay={setSelectedDay}
    />
  );
  const timeline = (
    <DayTimeline
      day={selectedDay}
      events={events}
      onCreateAt={(start) => setEventDialog({ open: true, start })}
      onSelectEvent={(event) => setEventDialog({ open: true, event })}
    />
  );
  const todoList = (
    <TodoList
      todos={pendingTodos}
      onAdd={() => setTodoDialog({ open: true })}
      onEdit={(todo) => setTodoDialog({ open: true, todo })}
    />
  );
  const dialogs = (
    <>
      <EventDialog
        open={eventDialog.open}
        event={eventDialog.open ? eventDialog.event : null}
        defaultStart={eventDialog.open ? eventDialog.start : null}
        onClose={() => setEventDialog({ open: false })}
      />
      <TodoDialog
        open={todoDialog.open}
        todo={todoDialog.open ? todoDialog.todo : null}
        onClose={() => setTodoDialog({ open: false })}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold">
              {translate("crm.personal.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="flex flex-col gap-6 pb-6">
            <NotificationPermissionButton />
            {calendar}
            {timeline}
            {todoList}
            <LifeWheelCard />
          </div>
        </MobileContent>
        {dialogs}
      </>
    );
  }

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sprout className="size-6" />
            {translate("crm.personal.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate("crm.personal.subtitle")} ·{" "}
            <span className="inline-block first-letter:uppercase">
              {format(new Date(), "PPPP", { locale: dateFnsLocale })}
            </span>
          </p>
        </div>
        <NotificationPermissionButton />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
        <Card>
          <CardContent className="flex flex-col gap-6">
            {calendar}
            {todoList}
          </CardContent>
        </Card>
        <Card>
          <CardContent>{timeline}</CardContent>
        </Card>
      </div>

      <LifeWheelCard />
      {dialogs}
    </div>
  );
};

PersonalPage.path = "/personal";
